import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { store } from './store/store';

// Auth Components
import AuthGuard from './components/auth/AuthGuard';
import AuthProvider from './components/auth/AuthProvider';
import LoginPage from './pages/auth/LoginPage';


// Import Layout and Components
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/dashboard/Dashboard';
import UnifiedQueueDashboard from './pages/dashboard/UnifiedQueueDashboard';
import QueueDisplay from './pages/medical/QueueDisplay';

// Import Modular Routes
import { getHrdRoutes } from './routes/HrdRoutes';
import { getLaboratoryRoutes } from './routes/LaboratoryRoutes';
import { getFinanceRoutes } from './routes/FinanceRoutes';
import { getLaporanRoutes } from './routes/LaporanRoutes';

// Lazy load components for better performance
// SaaS Public Component
const LandingPage = React.lazy(() => import('./pages/public/LandingPage'));
const SuperAdminDashboard = React.lazy(() => import('./pages/superadmin/SuperAdminDashboard'));
const SuperAdminLogin = React.lazy(() => import('./pages/superadmin/SuperAdminLogin'));

const PerawatDashboard = React.lazy(() => import('./pages/perawat/PerawatDashboard'));
const PerawatAssessment = React.lazy(() => import('./pages/perawat/PerawatAssessment'));
const PerawatWorkload = React.lazy(() => import('./pages/perawat/PerawatWorkload'));
const PerawatNotes = React.lazy(() => import('./pages/perawat/PerawatNotes'));

const DokterDashboard = React.lazy(() => import('./pages/dokter/DokterDashboard'));
const DokterExamination = React.lazy(() => import('./pages/dokter/DokterExamination'));
const DokterPrescriptions = React.lazy(() => import('./pages/dokter/DokterPrescriptions'));

const KebidananDashboard = React.lazy(() => import('./pages/kebidanan/KebidananDashboard'));

const DoctorOrderExecution = React.lazy(() => import('./pages/medical/DoctorOrderExecution_fixed'));
const DentalExamination = React.lazy(() => import('./pages/medical/DentalExamination'));
const DoctorOrders = React.lazy(() => import('./pages/medical/DoctorOrders'));
const MedicalCertificates = React.lazy(() => import('./pages/medical/MedicalCertificates'));
const MidwiferyExamination = React.lazy(() => import('./pages/medical/MidwiferyExamination'));
const NurseTest = React.lazy(() => import('./pages/medical/NurseTest'));
const DoctorQueue = React.lazy(() => import('./pages/medical/DoctorQueue'));
const DentalQueue = React.lazy(() => import('./pages/medical/DentalQueue'));
const MidwifeQueue = React.lazy(() => import('./pages/medical/MidwifeQueue'));


const SupportingExaminationsList = React.lazy(() => import('./pages/supporting-examinations/SupportingExaminationsList'));
const NewSupportingExamination = React.lazy(() => import('./pages/supporting-examinations/NewSupportingExamination'));
const ViewSupportingExamination = React.lazy(() => import('./pages/supporting-examinations/ViewSupportingExamination'));
const SupportingExaminationResultsHistory = React.lazy(() => import('./pages/supporting-examinations/SupportingExaminationResultsHistory'));

// Inventory Module Import
const InventoryModule = React.lazy(() => import('./pages/inventory'));
const OrderApprovalPage = React.lazy(() => import('./pages/inventory/OrderApprovalPage'));

// Master Data Imports
const ReferralPartnerMaster = React.lazy(() => import('./pages/master/ReferralPartnerMaster'));
const TravelMaster = React.lazy(() => import('./pages/master/TravelMaster'));
const CompanyMaster = React.lazy(() => import('./pages/master/CompanyMaster'));
const PromotionMaster = React.lazy(() => import('./pages/master/PromotionMaster'));

// Project Management Imports
const ProjectDashboard = React.lazy(() => import('./pages/project/ProjectDashboard'));
const KanbanBoard = React.lazy(() => import('./pages/project/KanbanBoard'));




const ServicePricing = React.lazy(() => import('./pages/management/ServicePricing'));
const AdminFeeSettings = React.lazy(() => import('./pages/management/AdminFeeSettings'));

// HRD Module Extracted to routes/HrdRoutes.tsx

// Laboratory Module Extracted to routes/LaboratoryRoutes.tsx

const RadiologyDashboard = React.lazy(() => import('./pages/radiology/RadiologyDashboard'));
const RadiologyRequests = React.lazy(() => import('./pages/radiology/RadiologyRequests'));
const RadiologyResults = React.lazy(() => import('./pages/radiology/RadiologyResults'));
const RadiologyResultsHistory = React.lazy(() => import('./pages/radiology/RadiologyResultsHistory'));
const RadiologyReports = React.lazy(() => import('./pages/radiology/RadiologyReports'));
const RadiologyQueue = React.lazy(() => import('./pages/radiology/RadiologyQueue'));
const RadiologySettings = React.lazy(() => import('./pages/radiology/RadiologySettings'));

const PharmacyDashboard = React.lazy(() => import('./pages/pharmacy/PharmacyDashboard'));
// Force HMR reload for MedicationManagement
const MedicationManagement = React.lazy(() => import('./pages/pharmacy/MedicationManagement'));
const PharmacyQueue = React.lazy(() => import('./pages/pharmacy/PharmacyQueue'));
const PharmacyPatientQueue = React.lazy(() => import('./pages/pharmacy/PharmacyPatientQueue'));
const PharmacyOrders = React.lazy(() => import('./pages/pharmacy/PharmacyOrders'));
const DirectSales = React.lazy(() => import('./pages/pharmacy/direct-sales/DirectSales'));

// Finance Module Extracted to routes/FinanceRoutes.tsx

const Reports = React.lazy(() => import('./pages/reports/Reports'));
const FinancialReports = React.lazy(() => import('./pages/reports/FinancialReports'));
const PatientAnalytics = React.lazy(() => import('./pages/reports/PatientAnalytics'));
const MedicalReports = React.lazy(() => import('./pages/reports/MedicalReports'));
const LaboratoryAnalytics = React.lazy(() => import('./pages/reports/LaboratoryAnalytics'));
const ReportsRadiology = React.lazy(() => import('./pages/reports/RadiologyReports'));
const OperationalMetrics = React.lazy(() => import('./pages/reports/OperationalMetrics'));
const CompanyRevenueReports = React.lazy(() => import('./pages/reports/CompanyRevenueReports'));

// Patient Module Imports
const PatientList = React.lazy(() => import('./pages/patients/PatientList'));
const PatientDetails = React.lazy(() => import('./pages/patients/PatientDetails'));
const PatientRegistration = React.lazy(() => import('./pages/patients/PatientRegistration'));
const PatientImport = React.lazy(() => import('./pages/patients/PatientImport'));
const PatientSearch = React.lazy(() => import('./pages/patients/PatientSearch'));
const PatientPayment = React.lazy(() => import('./pages/patients/PatientPayment'));

// Cashier Module Import
const CashierDashboard = React.lazy(() => import('./pages/cashier/CashierDashboard'));
const CancellationLogsPage = React.lazy(() => import('./pages/cashier/CancellationLogsPage'));

// Marketing Module Imports
const MarketingDashboard = React.lazy(() => import('./pages/marketing/MarketingDashboard'));
const MarketingAnalisa = React.lazy(() => import('./pages/marketing/MarketingAnalisa'));
const ManagerMarketing = React.lazy(() => import('./pages/marketing/ManagerMarketing'));
const TargetManagement = React.lazy(() => import('./pages/marketing/TargetManagement'));
const MarketingUserManagement = React.lazy(() => import('./pages/marketing/MarketingUserManagement'));
const WeeklyPlanManagement = React.lazy(() => import('./pages/marketing/WeeklyPlanManagement'));
const MonthlyPlanManagement = React.lazy(() => import('./pages/marketing/MonthlyPlanManagement'));
const CashbackRecap = React.lazy(() => import('./pages/marketing/CashbackRecap'));

// Accreditation Module Imports
const AccreditationDashboard = React.lazy(() => import('./pages/accreditation/AccreditationDashboard'));
const DocumentsPage = React.lazy(() => import('./pages/accreditation/DocumentsPage'));
const AssessmentsPage = React.lazy(() => import('./pages/accreditation/AssessmentsPage'));
const TemplateViewer = React.lazy(() => import('./pages/accreditation/TemplateViewer'));
const DocumentTemplateManager = React.lazy(() => import('./pages/accreditation/DocumentTemplateManager'));
const SelfAssessmentTool = React.lazy(() => import('./pages/accreditation/SelfAssessmentTool'));

// User Management Module Imports
const UserManagement = React.lazy(() => import('./pages/user-management/UserManagement'));
const RolesPermissions = React.lazy(() => import('./pages/user-management/RolesPermissions'));
const ClinicSettings = React.lazy(() => import('./pages/user-management/ClinicSettings'));

// Absen Module
const Absen = React.lazy(() => import('./pages/absen/Absen'));

// Portal Karyawan Module
const LeaveRequestPage = React.lazy(() => import('./pages/employee/LeaveRequestPage'));
const ReimbursementPage = React.lazy(() => import('./pages/employee/ReimbursementPage'));
const PayslipPage = React.lazy(() => import('./pages/employee/PayslipPage'));
const OrderBarangPage = React.lazy(() => import('./pages/employee/OrderBarangPage'));
const EmployeeAttendanceRecap = React.lazy(() => import('./pages/employee/EmployeeAttendanceRecap'));

// Administrasi Module
const SatuSehatPage = React.lazy(() => import('./pages/administrasi/SatuSehatPage'));
const ProlanisPage = React.lazy(() => import('./pages/administrasi/ProlanisPage'));
const KbkPage = React.lazy(() => import('./pages/administrasi/KbkPage'));
const ActivityLog = React.lazy(() => import('./pages/administrasi/ActivityLog'));

// Photo Data Module
const PhotoDataPage = React.lazy(() => import('./pages/PhotoData/PhotoDataPage').then(m => ({ default: m.PhotoDataPage })));

// Create QueryClient outside component to prevent re-creation on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Compact loading component for Suspense — keeps sidebar/header visible
const LoadingComponent = () => (
  <div className="flex items-center justify-center py-32 animate-fade-in">
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100"></div>
        <div className="absolute top-0 left-0 h-10 w-10 rounded-full border-4 border-transparent border-t-primary-500 animate-spin"></div>
      </div>
      <p className="text-sm text-gray-400 font-medium">Memuat...</p>
    </div>
  </div>
);

// Main App Component
const App: React.FC = () => {

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <Toaster position="top-right" />
            <Suspense fallback={<LoadingComponent />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/daftar-klinik" element={<LandingPage />} />
                <Route path="/queue-display" element={<QueueDisplay />} />
                <Route path="/queue-display/:serviceType" element={<QueueDisplay />} />
                <Route path="/queue-dashboard" element={<UnifiedQueueDashboard />} />

                {/* Super Admin Route */}
                <Route path="/superadmin/login" element={<SuperAdminLogin />} />
                <Route path="/superadmin/dashboard" element={
                  <AuthGuard requiredRoles={['superadmin']}>
                    <SuperAdminDashboard />
                  </AuthGuard>
                } />

                {/* Protected Routes with Dashboard Layout */}
                <Route element={
                  <AuthGuard>
                    <DashboardLayout />
                  </AuthGuard>
                }>
                  {/* Administrasi Routes */}
                  <Route path="/administrasi/satusehat" element={<SatuSehatPage />} />
                  <Route path="/administrasi/satusehat-sync" element={<SatuSehatPage />} />
                  <Route path="/administrasi/prolanis" element={<ProlanisPage />} />
                  <Route path="/administrasi/kbk" element={<KbkPage />} />
                  <Route path="/administrasi/activity-log" element={<ActivityLog />} />
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/dashboard/queue" element={<UnifiedQueueDashboard />} />

                  {/* Cashier Routes */}
                  <Route path="/cashier" element={<CashierDashboard />} />
                  <Route path="/cashier/cancellation-logs" element={<CancellationLogsPage />} />

                  {/* Photo Data Routes */}
                  <Route path="/photo-data" element={<PhotoDataPage />} />

                  {/* Patient Routes */}
                  <Route path="/patients" element={<PatientList />} />
                  <Route path="/patients/list" element={<PatientList />} />
                  <Route path="/patients/register" element={<PatientRegistration />} />
                  <Route path="/patients/import" element={<PatientImport />} />
                  <Route path="/patients/search" element={<PatientSearch />} />
                  <Route path="/patients/:id" element={<PatientDetails />} />
                  <Route path="/patients/:id/payment" element={<PatientPayment />} />

                  {/* Perawat Module Routes */}
                  <Route path="/perawat" element={<PerawatDashboard />} />
                  <Route path="/perawat/dashboard" element={<PerawatDashboard />} />
                  <Route path="/perawat/assessment" element={<PerawatAssessment />} />
                  <Route path="/perawat/workload" element={<PerawatWorkload />} />
                  <Route path="/perawat/notes" element={<PerawatNotes />} />

                  {/* Dokter Module Routes */}
                  <Route path="/dokter" element={<DokterDashboard />} />
                  <Route path="/dokter/dashboard" element={<DokterDashboard />} />
                  <Route path="/dokter/examination" element={<DokterExamination />} />
                  <Route path="/dokter/prescriptions" element={<DokterPrescriptions />} />
                  <Route path="/dokter/queue" element={<DoctorQueue />} />

                  {/* Dental Module Routes */}
                  <Route path="/dental" element={<DentalQueue />} />
                  <Route path="/dental/queue" element={<DentalQueue />} />
                  <Route path="/dental/examination" element={<DentalExamination />} />

                  {/* Legacy Medical Routes */}
                  <Route path="/medical/nurse/test" element={<NurseTest />} />
                  <Route path="/medical/dental/examination" element={<DentalExamination />} />
                  <Route path="/medical/dental/queue" element={<DentalQueue />} />
                  <Route path="/medical/doctor/orders" element={<DoctorOrderExecution />} />
                  <Route path="/medical/doctor/certificates" element={<MedicalCertificates />} />
                  {/* Kebidanan Module Routes */}
                  <Route path="/kebidanan" element={<KebidananDashboard />} />
                  <Route path="/kebidanan/dashboard" element={<KebidananDashboard />} />
                  <Route path="/kebidanan/examination" element={<MidwiferyExamination />} />
                  <Route path="/kebidanan/queue" element={<MidwifeQueue />} />

                  {/* Legacy Midwife Routes - for backward compatibility */}
                  <Route path="/medical/midwife" element={<KebidananDashboard />} />
                  <Route path="/medical/midwife/dashboard" element={<KebidananDashboard />} />
                  <Route path="/medical/midwife/examination" element={<MidwiferyExamination />} />
                  <Route path="/medical/nurse/doctor-orders" element={<DoctorOrders />} />

                  {/* Legacy routes for backward compatibility */}
                  <Route path="/medical/nurse/dashboard" element={<PerawatDashboard />} />
                  <Route path="/medical/nurse/assessment" element={<PerawatAssessment />} />
                  <Route path="/medical/nurse/workload" element={<PerawatWorkload />} />
                  <Route path="/medical/nursing/notes" element={<PerawatNotes />} />
                  <Route path="/medical/doctor/dashboard" element={<DokterDashboard />} />
                  <Route path="/medical/doctor/examination" element={<DokterExamination />} />
                  <Route path="/medical/prescription" element={<DokterPrescriptions />} />

                  {/* Supporting Examinations */}
                  <Route path="/supporting-examinations" element={<SupportingExaminationsList />} />
                  <Route path="/supporting-examinations/new" element={<NewSupportingExamination />} />
                  <Route path="/supporting-examinations/results-history" element={<SupportingExaminationResultsHistory />} />
                  <Route path="/supporting-examinations/:id" element={<ViewSupportingExamination />} />
                  <Route path="/supporting-examinations/:id/edit" element={<ViewSupportingExamination />} />

                  {/* Inventory */}
                  <Route path="/inventory/order-approval" element={<OrderApprovalPage />} />
                  <Route path="/inventory/*" element={<InventoryModule />} />

                  {/* Portal Karyawan */}
                  <Route path="/employee/leave" element={<LeaveRequestPage />} />
                  <Route path="/employee/attendance-recap" element={<EmployeeAttendanceRecap />} />
                  <Route path="/employee/reimbursement" element={<ReimbursementPage />} />
                  <Route path="/employee/payslip" element={<PayslipPage />} />
                  <Route path="/employee/order-barang" element={<OrderBarangPage />} />

                  {/* Management & Master Data */}
                  <Route path="/master/referrals" element={<ReferralPartnerMaster />} />
                  <Route path="/master/travel" element={<TravelMaster />} />
                  <Route path="/master/companies" element={<CompanyMaster />} />
                  <Route path="/master/promotions" element={<PromotionMaster />} />
                  <Route path="/management/pricing" element={<ServicePricing />} />
                  <Route path="/management/admin-fee" element={<AdminFeeSettings />} />

                  {/* Project Management Routes */}
                  <Route path="/project" element={<ProjectDashboard />} />
                  <Route path="/project/dashboard" element={<ProjectDashboard />} />
                  <Route path="/project/board" element={<KanbanBoard />} />

                  {/* Extracted Modular Routes */}
                  {getHrdRoutes()}
                  {getLaboratoryRoutes()}

                  {/* Radiology Routes */}
                  <Route path="/radiology" element={<RadiologyDashboard />} />
                  <Route path="/radiology/requests" element={<RadiologyRequests />} />
                  <Route path="/radiology/results" element={<RadiologyResults />} />
                  <Route path="/radiology/results-history" element={<RadiologyResultsHistory />} />
                  <Route path="/radiology/reports" element={<RadiologyReports />} />
                  <Route path="/radiology/queue" element={<RadiologyQueue />} />
                  <Route path="/radiology/settings" element={<RadiologySettings />} />

                  {/* Pharmacy Routes */}
                  <Route path="/pharmacy" element={<PharmacyDashboard />} />
                  <Route path="/pharmacy/medications" element={<MedicationManagement />} />
                  <Route path="/pharmacy/queue" element={<PharmacyQueue />} />
                  <Route path="/pharmacy/patient-queue" element={<PharmacyPatientQueue />} />
                  <Route path="/pharmacy/orders" element={<PharmacyOrders />} />
                  <Route path="/pharmacy/direct-sales" element={<DirectSales />} />

                  {/* Finance Routes */}
                  {getFinanceRoutes()}

                  {/* Marketing Routes */}
                  <Route path="/marketing" element={<MarketingDashboard />} />
                  <Route path="/marketing/dashboard" element={<MarketingDashboard />} />
                  <Route path="/marketing/analisa" element={<MarketingAnalisa />} />
                  <Route path="/marketing/manager" element={<ManagerMarketing />} />
                  <Route path="/marketing/targets" element={<TargetManagement />} />
                  <Route path="/marketing/users" element={<MarketingUserManagement />} />
                  <Route path="/marketing/weekly-plans" element={<WeeklyPlanManagement />} />
                  <Route path="/marketing/monthly-plans" element={<MonthlyPlanManagement />} />
                  <Route path="/marketing/cashback" element={<CashbackRecap />} />

                  {/* Reports Routes */}
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/reports/financial" element={<FinancialReports />} />
                  <Route path="/reports/patient" element={<PatientAnalytics />} />
                  <Route path="/reports/medical" element={<MedicalReports />} />
                  <Route path="/reports/laboratory" element={<LaboratoryAnalytics />} />
                  <Route path="/reports/radiology" element={<ReportsRadiology />} />
                  <Route path="/reports/operational" element={<OperationalMetrics />} />
                  <Route path="/reports/company-revenue" element={<CompanyRevenueReports />} />
                  {getLaporanRoutes()}

                  {/* Accreditation Routes */}
                  <Route path="/accreditation" element={<AccreditationDashboard />} />
                  <Route path="/accreditation/dashboard" element={<AccreditationDashboard />} />
                  <Route path="/accreditation/documents" element={<DocumentsPage />} />
                  <Route path="/accreditation/assessments" element={<AssessmentsPage />} />
                  <Route path="/accreditation/templates" element={<TemplateViewer />} />
                  <Route path="/accreditation/template-manager" element={<DocumentTemplateManager />} />
                  <Route path="/accreditation/self-assessment" element={<SelfAssessmentTool />} />

                  {/* User Management Routes */}
                  <Route path="/user-management/users" element={<UserManagement />} />
                  <Route path="/user-management/roles" element={<RolesPermissions />} />
                  <Route path="/user-management/clinic-settings" element={<ClinicSettings />} />

                  {/* Absen Routes */}
                  <Route path="/absen" element={<Absen />} />
                </Route>

                {/* Catch-all route to prevent blank screens */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;